import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, withCtx as _withCtx, withModifiers as _withModifiers, Fragment as _Fragment, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-4 text-left",
  "data-testid": "pre-allocation-kanban-tab"
}
const _hoisted_2 = /*#__PURE__*/_createStaticVNode("<div class=\"flex flex-wrap items-center justify-between gap-3\"><p class=\"text-[13px] text-[#52526f] max-w-3xl\"> Médicos <b class=\"text-textPrimary\">pré-alocados</b> esperando documentação. Ordenado pelo plantão mais próximo. Os cartões andam sozinhos conforme o status dos documentos. </p><div class=\"flex items-center gap-3 text-[11px] text-[#52526f]\"><span class=\"flex items-center gap-1\"><span class=\"w-2.5 h-2.5 rounded-sm bg-red-50 border border-red-300\"></span> ≤ 24h do plantão </span><span class=\"flex items-center gap-1\"><span class=\"w-2.5 h-2.5 rounded-sm bg-orange-50 border border-orange-300\"></span> ≤ 72h </span></div></div>", 1)
const _hoisted_3 = {
  key: 0,
  class: "h-[320px] rounded-lg bg-gray-100 animate-pulse",
  "data-testid": "pre-allocation-kanban-skeleton"
}
const _hoisted_4 = ["onDrop"]

import { reactive, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiLockOutline, mdiViewColumnOutline } from '@mdi/js'

import EmptyState from '@/components/designSystem/EmptyState/EmptyState.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import QueueKanban from '@/components/designSystem/QueueViews/QueueKanban.vue'
import type { QueueKanbanColumn } from '@/components/designSystem/QueueViews/types'
import { pendingReasonText } from '@/components/ProfessionalDocuments/labels'
import { useErrorToast } from '@/composables/useErrorToast'
import { usePreAllocationKanbanStore } from '@/pinia-store/usePreAllocationKanbanStore'
import { useProfessionalDocumentCatalogStore } from '@/pinia-store/useProfessionalDocumentCatalogStore'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import { PreAllocationColumn } from '@/types/qualification-eligibility.types'
import type { EligibilityReason, PreAllocationCard } from '@/types/qualification-eligibility.types'

import { KANBAN_LOAD_ERROR, MOVE_ERROR } from '../constants'
import {
  KANBAN_TEST_ID,
  PRE_ALLOCATION_COLUMNS,
  parseKanbanColumnKey,
  stationsByStart
} from '../model'
import PreAllocationBlockedModal from './PreAllocationBlockedModal.vue'
import type { BlockedItem } from './PreAllocationBlockedModal.vue'
import PreAllocationCardView from './PreAllocationCard.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'PreAllocationKanban',
  emits: ["reviewDocuments"],
  setup(__props, { emit }) {



const store = usePreAllocationKanbanStore()
const catalog = useProfessionalDocumentCatalogStore()
const { sortedCards, isLoading, isLoaded, isUnavailable, hasNoScope, loadError, movingId } =
  storeToRefs(store)
const { notifyError } = useErrorToast()

const columns: QueueKanbanColumn<PreAllocationCard>[] = PRE_ALLOCATION_COLUMNS.map((column) => ({
  ...column,
  matches: (card: PreAllocationCard) => card.column === column.key
}))

const draggingId = ref<number | null>(null)
const blocked = reactive<{
  open: boolean
  card: PreAllocationCard | null
  target: PreAllocationColumn
  items: BlockedItem[]
}>({ open: false, card: null, target: PreAllocationColumn.RESOLVED, items: [] })

const sameReason = (a: EligibilityReason, b: EligibilityReason) =>
  a.field === b.field && a.key === b.key

/** Nome que o cartão já trouxe; item novo (cartão desatualizado) busca no catálogo. */
async function toBlockedItems(
  card: PreAllocationCard,
  missing: EligibilityReason[]
): Promise<BlockedItem[]> {
  const nameInCard = (reason: EligibilityReason) =>
    card.missing.find((item) => sameReason(item, reason))?.name
  if (missing.some((reason) => !nameInCard(reason))) {
    // fallback intencional: sem catálogo o pop-up mostra a chave do item, que segue visível
    await catalog.ensureLoaded().catch(() => undefined)
  }
  return missing.map((reason) => {
    const status = reason.status ?? ProfessionalDocumentStatus.PENDING
    return {
      key: `${reason.field}:${reason.key}`,
      name: nameInCard(reason) ?? catalog.reasonName(reason),
      status,
      detail: pendingReasonText({
        status,
        pendingReason: reason.pendingReason,
        expiresAt: reason.expiresAt
      })
    }
  })
}

async function tryMove(card: PreAllocationCard, target: PreAllocationColumn) {
  try {
    const outcome = await store.move(card, target)
    if (outcome.kind !== 'blocked') return
    const items = await toBlockedItems(card, outcome.missing)
    Object.assign(blocked, { open: true, card, target, items })
  } catch (error) {
    notifyError(error, MOVE_ERROR)
  }
}

function onDrop(event: DragEvent) {
  const card = sortedCards.value.find((item) => item.id === draggingId.value)
  draggingId.value = null
  const columnEl = (event.target as HTMLElement | null)?.closest(
    `[data-testid^="${KANBAN_TEST_ID}-column-"]`
  )
  const target = parseKanbanColumnKey(columnEl?.getAttribute('data-testid') ?? null)
  if (card && target) void tryMove(card, target)
}

function onReview() {
  blocked.open = false
  if (blocked.card) emit('reviewDocuments', blocked.card.professional)
}

// Começa no setup: o primeiro render já sai com o skeleton, sem piscar o vazio (R25).
void store.fetch()

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(isUnavailable))
      ? (_openBlock(), _createBlock(EmptyState, {
          key: 0,
          icon: _unref(mdiViewColumnOutline),
          title: "Demandas urgentes indisponíveis",
          description: "A revisão de documentos ainda não foi liberada neste ambiente.",
          variant: "dashed"
        }, null, 8, ["icon"]))
      : (_unref(hasNoScope))
        ? (_openBlock(), _createBlock(EmptyState, {
            key: 1,
            icon: _unref(mdiLockOutline),
            title: "Nenhuma escala para acompanhar",
            description: "Você não tem acesso a nenhuma escala com revisão de documentos. Peça a um administrador da sua empresa para liberar o acesso.",
            variant: "dashed"
          }, null, 8, ["icon"]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _hoisted_2,
            (_unref(isLoading) && !_unref(isLoaded))
              ? (_openBlock(), _createElementBlock("div", _hoisted_3))
              : (_unref(loadError))
                ? (_openBlock(), _createBlock(ReportErrorState, {
                    key: 1,
                    title: _unref(KANBAN_LOAD_ERROR),
                    message: _unref(loadError) === _unref(KANBAN_LOAD_ERROR) ? null : _unref(loadError),
                    onRetry: _cache[0] || (_cache[0] = ($event: any) => (_unref(store).fetch()))
                  }, null, 8, ["title", "message"]))
                : (!_unref(sortedCards).length)
                  ? (_openBlock(), _createBlock(EmptyState, {
                      key: 2,
                      icon: _unref(mdiViewColumnOutline),
                      title: "Nenhuma demanda urgente",
                      description: "Quando um médico for pré-alocado com documento pendente ou em análise, a demanda aparece aqui.",
                      variant: "dashed"
                    }, null, 8, ["icon"]))
                  : (_openBlock(), _createElementBlock("div", {
                      key: 3,
                      onDragover: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"])),
                      onDrop: _withModifiers(onDrop, ["prevent"]),
                      onDragend: _cache[4] || (_cache[4] = ($event: any) => (draggingId.value = null))
                    }, [
                      _createVNode(QueueKanban, {
                        rows: _unref(sortedCards),
                        columns: _unref(columns),
                        "row-key": (row) => row.id,
                        "empty-column-text": "Nenhuma demanda nesta etapa.",
                        "test-id": _unref(KANBAN_TEST_ID)
                      }, {
                        card: _withCtx(({ row }) => [
                          _createVNode(PreAllocationCardView, {
                            card: row,
                            "is-moving": _unref(movingId) === row.id,
                            onDragStart: _cache[1] || (_cache[1] = ($event: any) => (draggingId.value = $event.id)),
                            onTryResolve: _cache[2] || (_cache[2] = ($event: any) => (tryMove($event, _unref(PreAllocationColumn).RESOLVED)))
                          }, null, 8, ["card", "is-moving"])
                        ]),
                        _: 1
                      }, 8, ["rows", "columns", "row-key", "test-id"])
                    ], 40, _hoisted_4))
          ], 64)),
    _createVNode(PreAllocationBlockedModal, {
      modelValue: blocked.open,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((blocked.open) = $event)),
      "professional-name": blocked.card?.professional.name ?? '',
      "target-column": blocked.target,
      items: blocked.items,
      "nearest-station": blocked.card ? (_unref(stationsByStart)(blocked.card)[0] ?? null) : null,
      onReview: onReview
    }, null, 8, ["modelValue", "professional-name", "target-column", "items", "nearest-station"])
  ]))
}
}

})